import { useTranslation } from 'react-i18next';
import { t as translate } from 'i18n';
import React, { useState } from 'react';
import {
  Box,
  FormControlLabel,
  FormHelperText,
  IconButton,
  InputAdornment,
  MenuItem,
  Switch,
  TextField,
} from '@mui/material';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';

// MUI's stock placeholder (0.42 light / 0.5 dark) sits at ~2.7:1 — fine for a
// throwaway hint, too faint for a value the reader is meant to take in. 0.6
// clears 4.5:1 in both themes while still reading at half the strength of
// typed text (10:1), which is the whole point: saved, not entered.
const PLACEHOLDER_SX = { '& input::placeholder': { opacity: 0.6 } };

/** A stored value as display text — shared with the read-only config card. */
export function formatConfigValue(value) {
  if (value === null || value === undefined) return '';
  if (typeof value === 'boolean') return value ? translate('shell.yes') : translate('shell.no');
  if (Array.isArray(value)) return value.map(formatConfigValue).filter(Boolean).join(', ');
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
}

/**
 * Renders a form from a JSON Schema (draft-7 subset).
 *
 * Props:
 * Field order comes from `schema['x-order']` when the schema declares it.
 * Without it the order is whatever the object yields, and a schema stored in a
 * jsonb column yields its keys sorted by length — which puts `tables` above
 * `dataset` and the credential in the middle of the form. Keys the array does
 * not mention keep their existing relative order, after the ones it does.
 *
 * Props:
 *   schema      — JSON Schema object
 *   value       — { [fieldName]: any }
 *   onChange    — (newValue: object) => void
 *   errors      — { [fieldName]: string } (optional validation errors)
 *   configuredFields — string[] (fields already saved; the ones with no value
 *                   to show say "unchanged" instead)
 *   currentValues — { [fieldName]: any } saved values of the NON-CREDENTIAL
 *                   fields (the API's visible_config). Shown as each field's
 *                   placeholder, never as its value — see below.
 */
function orderedEntries(properties, order) {
  const entries = Object.entries(properties);
  if (!Array.isArray(order) || order.length === 0) return entries;
  const rank = new Map(order.map((key, index) => [key, index]));
  const place = (key) => (rank.has(key) ? rank.get(key) : order.length);
  return entries
    .map((entry, index) => ({ entry, index }))
    .sort((a, b) => place(a.entry[0]) - place(b.entry[0]) || a.index - b.index)
    .map(({ entry }) => entry);
}

export default function JsonSchemaForm({
  schema = {},
  value = {},
  onChange,
  errors = {},
  configuredFields = [],
  currentValues = {},
}) {
  const { t: translate } = useTranslation();
  const [showPassword, setShowPassword] = useState({});

  if (!schema.properties) return null;

  const properties = schema.properties;
  const required = schema.required || [];

  const handleChange = (key, newVal) => {
    onChange({ ...value, [key]: newVal });
  };

  const toggleShow = (key) =>
    setShowPassword((prev) => ({ ...prev, [key]: !prev[key] }));

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
      {orderedEntries(properties, schema['x-order']).map(([key, prop]) => {
        const fieldLabel = prop.title || key;
        const isRequired = required.includes(key);
        const isConfigured = configuredFields.includes(key);
        const fieldError = errors[key];
        const currentValue = value[key] ?? '';
        const stored = currentValues[key];
        const storedText = formatConfigValue(stored);
        // A saved value is shown as the PLACEHOLDER, never prefilled: the API
        // merge reads an omitted or blank field as "keep this one", so a
        // prefilled box would resend every value the user never touched —
        // and there'd be no way to tell a real edit from a stale echo.
        // A saved field with nothing to show (a credential) says so instead.
        const placeholder = storedText || (isConfigured ? translate('shell.unchanged') : undefined);
        // MUI hides a placeholder behind an un-shrunk label (they occupy the
        // same line), so a field that has one must float its label up front.
        const labelProps = placeholder ? { shrink: true } : undefined;

        // ── Boolean → Switch ─────────────────────────────────────────────
        if (prop.type === 'boolean') {
          // No placeholder on a switch — an untouched one has to sit at the
          // saved position, or the form would show every toggle as "off".
          const touched = value[key] !== undefined;
          const shown = touched ? currentValue : stored;
          return (
            <Box key={key}>
              <FormControlLabel
                control={
                  <Switch
                    checked={shown === true || shown === 'true'}
                    onChange={(e) => handleChange(key, e.target.checked)}
                  />
                }
                label={fieldLabel}
              />
              {prop.description && (
                <FormHelperText sx={{ ml: 0 }}>{prop.description}</FormHelperText>
              )}
            </Box>
          );
        }

        // ── Enum → Select ────────────────────────────────────────────────
        if (prop.enum) {
          // Also no placeholder: a closed list selects the saved option.
          return (
            <TextField
              key={key}
              select
              label={fieldLabel}
              value={currentValue || stored || prop.default || ''}
              onChange={(e) => handleChange(key, e.target.value)}
              required={isRequired}
              helperText={fieldError || prop.description}
              error={!!fieldError}
              fullWidth
            >
              {prop.enum.map((opt) => (
                <MenuItem key={opt} value={opt}>
                  {opt}
                </MenuItem>
              ))}
            </TextField>
          );
        }

        // ── Password string ──────────────────────────────────────────────
        if (prop.format === 'password') {
          const isShowing = showPassword[key];
          return (
            <TextField
              key={key}
              label={fieldLabel}
              type={isShowing ? 'text' : 'password'}
              value={currentValue}
              onChange={(e) => handleChange(key, e.target.value)}
              required={isRequired && !isConfigured}
              helperText={fieldError || prop.description}
              error={!!fieldError}
              fullWidth
              placeholder={placeholder}
              InputLabelProps={labelProps}
              sx={PLACEHOLDER_SX}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => toggleShow(key)} edge="end" aria-label={translate(isShowing ? 'shell.hidePassword' : 'shell.showPassword')}>
                      {isShowing ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          );
        }

        // ── Integer / Number ─────────────────────────────────────────────
        if (prop.type === 'integer' || prop.type === 'number') {
          return (
            <TextField
              key={key}
              label={fieldLabel}
              type="number"
              // The schema default only fills a field with nothing saved —
              // over a saved value it would read as the current setting.
              value={currentValue !== '' ? currentValue : (placeholder ? '' : (prop.default ?? ''))}
              onChange={(e) =>
                handleChange(key, e.target.value === '' ? '' : Number(e.target.value))
              }
              required={isRequired}
              helperText={fieldError || prop.description}
              error={!!fieldError}
              fullWidth
              placeholder={placeholder}
              InputLabelProps={labelProps}
              sx={PLACEHOLDER_SX}
            />
          );
        }

        // ── Plain string ─────────────────────────────────────────────────
        return (
          <TextField
            key={key}
            label={fieldLabel}
            value={currentValue}
            onChange={(e) => handleChange(key, e.target.value)}
            required={isRequired}
            helperText={fieldError || prop.description}
            error={!!fieldError}
            fullWidth
            placeholder={placeholder}
            InputLabelProps={labelProps}
            sx={PLACEHOLDER_SX}
          />
        );
      })}
    </Box>
  );
}
